<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- 헤더 -->
    <div class="mb-8">
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">내 게시글</h1>
          <p class="mt-2 text-gray-600">내가 작성한 게시글들을 관리하세요</p>
        </div>
        <RouterLink to="/posts/create" class="btn btn-primary btn-sm">
          새 게시글 작성
        </RouterLink>
      </div>
    </div>

    <!-- 통계 카드 -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <DocumentTextIcon class="w-6 h-6 text-blue-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">총 게시글</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalElements }}개</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <UserGroupIcon class="w-6 h-6 text-green-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">총 예약 수</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalReservations }}개</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <EyeIcon class="w-6 h-6 text-purple-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">활성 게시글</p>
            <p class="text-2xl font-bold text-gray-900">{{ activePosts }}개</p>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <BoardListToolbar
        v-model:pageSize="pageSize"
        :page-size-options="pageSizeOptions"
        :total-elements="totalElements"
        count-label="게시글"
        :show-page-size="true"
        @page-size-change="handlePageSizeChange"
      />
    </div>

    <!-- 게시글 목록 -->
    <div class="bg-white rounded-lg border shadow-sm">
      <div class="px-6 py-4 border-b">
        <h2 class="text-lg font-semibold text-gray-900">게시글 목록</h2>
      </div>

      <!-- 로딩 -->
      <div v-if="isLoading" class="p-6">
        <div class="space-y-4">
          <div v-for="n in 5" :key="n" class="animate-pulse">
            <div class="flex justify-between items-center p-4 border rounded-lg">
              <div class="flex-1">
                <div class="h-4 bg-gray-300 rounded mb-2 w-3/4"></div>
                <div class="h-3 bg-gray-300 rounded w-1/2"></div>
              </div>
              <div class="flex space-x-2">
                <div class="h-8 w-16 bg-gray-300 rounded"></div>
                <div class="h-8 w-16 bg-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 게시글 목록 -->
      <div v-else-if="posts.length > 0" class="divide-y">
        <div
          v-for="post in posts"
          :key="post.id"
          class="p-6 hover:bg-gray-50 transition-colors"
        >
          <div class="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <RouterLink
                  :to="`/posts/${post.id}`"
                  class="text-lg font-semibold text-gray-900 hover:text-primary-600 truncate"
                >
                  {{ post.title }}
                </RouterLink>
                <span
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                  :class="{
                    'bg-green-100 text-green-800': isActive(post),
                    'bg-gray-100 text-gray-800': !isActive(post)
                  }"
                >
                  {{ isActive(post) ? '활성' : '종료' }}
                </span>
              </div>
              
              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 mb-3">
                <div class="flex items-center min-w-0">
                  <MapPinIcon class="w-4 h-4 mr-1" />
                  <span class="min-w-0 break-words">{{ post.place }}</span>
                </div>
                <div class="flex items-center min-w-0">
                  <CalendarDaysIcon class="w-4 h-4 mr-1" />
                  <span class="min-w-0 break-words">
                    {{ formatDate(post.startDate) }} - {{ formatDate(post.endDate) }}
                  </span>
                </div>
                <div class="flex items-center min-w-0">
                  <UserGroupIcon class="w-4 h-4 mr-1" />
                  <span class="min-w-0 break-words">
                    {{ post.reservedSeats || 0 }} / {{ post.totalSeats || 0 }} 예약
                  </span>
                </div>
              </div>
              
              <p class="text-gray-600 text-sm line-clamp-2 break-words">
                {{ post.content }}
              </p>
            </div>

            <!-- 액션 버튼 -->
            <div class="flex flex-wrap gap-2 sm:ml-6">
              <RouterLink
                :to="`/posts/${post.id}`"
                class="btn btn-outline btn-sm"
              >
                보기
              </RouterLink>
              <RouterLink
                :to="`/posts/${post.id}/edit`"
                class="btn btn-outline btn-sm"
              >
                <PencilIcon class="w-4 h-4 mr-1" />
                수정
              </RouterLink>
              <button
                @click="showDeleteModal(post)"
                class="btn bg-red-600 hover:bg-red-700 text-white btn-sm"
              >
                <TrashIcon class="w-4 h-4 mr-1" />
                삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="p-12 text-center">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <DocumentTextIcon class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">작성한 게시글이 없습니다</h3>
        <p class="text-gray-600 mb-6">첫 번째 게시글을 작성해보세요.</p>
        <RouterLink to="/posts/create" class="btn btn-primary btn-sm">
          게시글 작성하기
        </RouterLink>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <div v-if="totalPages > 1" class="mt-8">
      <Pagination :current-page="currentPage" :total-pages="totalPages" @page-change="goToPage" />
    </div>

    <!-- 삭제 확인 모달 -->
    <div v-if="selectedPost" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-red-900">게시글 삭제 확인</h3>
          <button @click="selectedPost = null" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="bg-red-50 p-4 rounded-md">
            <ExclamationTriangleIcon class="w-5 h-5 text-red-400 inline mr-2" />
            <span class="text-red-800 font-medium">이 작업은 되돌릴 수 없습니다!</span>
          </div>
          
          <p class="text-gray-700">
            '{{ selectedPost.title }}' 게시글을 삭제하시겠습니까? 모든 댓글과 예약 정보도 함께 삭제됩니다.
          </p>
          
          <div class="flex gap-3 pt-4">
            <button
              @click="selectedPost = null"
              class="btn btn-outline btn-sm flex-1"
            >
              취소
            </button>
            <button
              @click="deletePost"
              :disabled="isDeleting"
              class="btn btn-sm bg-red-600 hover:bg-red-700 text-white flex-1"
              :class="{ 'opacity-50 cursor-not-allowed': isDeleting }"
            >
              <span v-if="isDeleting">삭제 중...</span>
              <span v-else>삭제하기</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import {
  DocumentTextIcon,
  UserGroupIcon,
  EyeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { postApi } from '@/services/api'
import type { Post } from '@/types'
import { useToast } from 'vue-toastification'
import Pagination from '@/components/common/Pagination.vue'
import BoardListToolbar from '@/components/common/BoardListToolbar.vue'

const toast = useToast()

const posts = ref<Post[]>([])
const isLoading = ref(true)
const isDeleting = ref(false)
const selectedPost = ref<Post | null>(null)

// 페이지네이션
const currentPage = ref(0)
const totalPages = ref(0)
const totalElements = ref(0)
const pageSizeOptions = [6, 12, 18, 21]
const pageSize = ref(6)

// 통계 데이터
const totalReservations = computed(() => {
  return posts.value.reduce((sum, post) => sum + (post.reservedSeats || 0), 0)
})

const activePosts = computed(() => {
  const now = new Date()
  return posts.value.filter(post => new Date(post.endDate) > now).length
})

// 날짜 포맷
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

// 활성 상태 확인
const isActive = (post: Post) => {
  return new Date(post.endDate) > new Date()
}

// 내 게시글 목록 로드
const loadMyPosts = async (page = 0) => {
  try {
    isLoading.value = true
    const response = await postApi.getMyPosts({
      page,
      size: pageSize.value
    })
    
    posts.value = response.content
    currentPage.value = response.number
    totalPages.value = response.totalPages
    totalElements.value = response.totalElements
  } catch (error) {
    console.error('Failed to load my posts:', error)
    toast.error('게시글 목록을 불러오는데 실패했습니다.')
  } finally {
    isLoading.value = false
  }
}

// 페이지 이동
const goToPage = (page: number) => {
  loadMyPosts(page)
}

const handlePageSizeChange = () => {
  currentPage.value = 0
  loadMyPosts(0)
}

// 삭제 모달 표시
const showDeleteModal = (post: Post) => {
  selectedPost.value = post
}

// 게시글 삭제
const deletePost = async () => {
  if (!selectedPost.value) return
  
  try {
    isDeleting.value = true
    await postApi.deletePost(selectedPost.value.id)
    
    // 로컬 상태에서 제거
    const index = posts.value.findIndex(p => p.id === selectedPost.value!.id)
    if (index !== -1) {
      posts.value.splice(index, 1)
      totalElements.value--
    }
    
    toast.success('게시글이 삭제되었습니다.')
    selectedPost.value = null
    
    // 현재 페이지가 비었고 이전 페이지가 있으면 이전 페이지로 이동
    if (posts.value.length === 0 && currentPage.value > 0) {
      loadMyPosts(currentPage.value - 1)
    }
  } catch (error) {
    console.error('Failed to delete post:', error)
    toast.error('게시글 삭제에 실패했습니다.')
  } finally {
    isDeleting.value = false
  }
}

onMounted(() => {
  loadMyPosts()
})
</script>
